import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ShoppingCart, Check } from 'lucide-react';
import { Product } from '../../types/pos.types';

interface SelectedComplement {
  product: Product;
  quantity: number;
}

interface ComplementSelectorProps {
  isOpen: boolean;
  mainProduct: Product | null;
  complements: Product[];
  onClose: () => void;
  onConfirm: (mainProduct: Product, selectedComplements: SelectedComplement[]) => void;
}

export const ComplementSelector: React.FC<ComplementSelectorProps> = ({
  isOpen,
  mainProduct,
  complements,
  onClose,
  onConfirm
}) => {
  const [selectedComplements, setSelectedComplements] = useState<SelectedComplement[]>([]);
  const [currentStep, setCurrentStep] = useState<'additionals' | 'complements'>('additionals');

  // Separar adicionales de complementos normales
  const additionals = complements.filter(c => c.isAdditional);
  const normalComplements = complements.filter(c => !c.isAdditional);

  const handleToggleComplement = (complement: Product) => {
    setSelectedComplements(prev => {
      const isSelected = prev.some(item => item.product.id === complement.id);

      if (isSelected) {
        // Deseleccionar: remover del array
        return prev.filter(item => item.product.id !== complement.id);
      } else {
        // Seleccionar: agregar con cantidad 1
        return [...prev, { product: complement, quantity: 1 }];
      }
    });
  };

  const isSelected = (complementId: string): boolean => {
    return selectedComplements.some(item => item.product.id === complementId);
  };

  const getTotalComplementsPrice = (): number => {
    return selectedComplements.reduce((total, item) =>
      total + item.product.price, 0 // Solo sumar precio, ya que quantity siempre es 1
    );
  };

  const handleConfirm = () => {
    if (mainProduct) {
      onConfirm(mainProduct, selectedComplements);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedComplements([]);
    setCurrentStep('additionals');
    onClose();
  };

  const handleSkip = () => {
    if (currentStep === 'additionals') {
      // Si está en adicionales, pasar a complementos
      setCurrentStep('complements');
    } else {
      // Si está en complementos, confirmar sin más complementos
      if (mainProduct) {
        onConfirm(mainProduct, selectedComplements);
        handleClose();
      }
    }
  };

  const handleContinue = () => {
    if (currentStep === 'additionals') {
      // Pasar a la pantalla de complementos
      setCurrentStep('complements');
    } else {
      // Confirmar selección final
      handleConfirm();
    }
  };

  if (!mainProduct) return null;

  // Determinar qué productos mostrar según el paso actual
  const productsToShow = currentStep === 'additionals' ? additionals : normalComplements;
  const title = currentStep === 'additionals'
    ? '¿Deseas agregar adicionales?'
    : '¿Deseas agregar complementos?';
  const skipButtonText = currentStep === 'additionals'
    ? 'Omitir adicionales'
    : 'Omitir complementos';
  const continueButtonText = currentStep === 'additionals'
    ? 'Continuar'
    : 'Agregar al carrito';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            {title}
          </DialogTitle>
          <div className="text-sm text-gray-600 mt-2">
            <span className="font-medium">{mainProduct.name}</span> - S/ {mainProduct.price.toFixed(2)}
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Lista de complementos disponibles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {productsToShow.map(complement => {
              const selected = isSelected(complement.id);

              return (
                <Card
                  key={complement.id}
                  onClick={() => handleToggleComplement(complement)}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-lg active:scale-98 ${
                    selected
                      ? 'ring-2 ring-macartur-primary border-macartur-primary bg-macartur-primary/5'
                      : 'hover:border-gray-300 border-2 border-gray-200'
                  }`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 text-base">
                          {complement.name}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {complement.description}
                        </p>
                        <div className="mt-3">
                          <span className="font-bold text-lg text-macartur-primary">
                            S/ {complement.price.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Indicador de selección */}
                      <div className={`ml-3 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                        selected
                          ? 'bg-macartur-primary text-white'
                          : 'bg-gray-100 text-gray-400'
                      }`}>
                        {selected && <Check className="h-5 w-5" />}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

        </div>

        {/* Botones de acción - Touch friendly */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleSkip}
            className="flex-1 h-14 text-base font-semibold border-2 transition-all duration-200 active:scale-95"
          >
            {skipButtonText}
          </Button>
          <Button
            onClick={handleContinue}
            className="flex-1 h-14 text-base font-semibold bg-macartur-primary hover:bg-macartur-primary/90 transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
          >
            <ShoppingCart className="h-5 w-5" />
            {continueButtonText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};